
import { supabase } from '@/integrations/supabase/client';
import { ChatMessage, ChatGroup, ChatGroupUser } from '@/types/chat';
import { UnifiedUser } from '@/hooks/useUnifiedUser';
import { supabaseChatService } from './supabaseChatService';

export const unifiedChatService = {
  // Enhanced message sending that works for both user types
  async sendMessage(chatGroupId: string, message: string, currentUser: UnifiedUser, parentMessageId?: string): Promise<ChatMessage> {
    console.log('=== UNIFIED SEND MESSAGE ===');
    console.log('User:', currentUser.username, 'Type:', currentUser.userType);
    console.log('Group:', chatGroupId);
    console.log('Message:', message);
    console.log('Parent message ID:', parentMessageId || 'none (root message)');
    
    try {
      // Simplified validation - ensure user is in group (add if creator)
      await this.ensureUserInGroup(chatGroupId, currentUser);

      // Insert the message
      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          chat_group_id: chatGroupId,
          user_id: currentUser.id,
          message: message,
          parent_message_id: parentMessageId || null
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error sending message:', error);
        throw new Error(`Failed to send message: ${error.message}`);
      }

      console.log('✓ Message inserted successfully:', data.message_id);

      // If this is a reply, increment parent's reply_count
      if (parentMessageId) {
        console.log('Incrementing reply count for parent message:', parentMessageId);
        await this.incrementReplyCount(parentMessageId);
      }

      // Create the enhanced message with user profile
      const newMessage: ChatMessage = {
        ...data,
        user_profile: {
          username: currentUser.username,
          first_name: currentUser.username,
          last_name: '',
          role: currentUser.role
        }
      };

      // Create notifications for group members (don't block message sending if this fails)
      try {
        await this.createNotificationsForMessage(newMessage, chatGroupId, currentUser);
      } catch (notificationError) {
        console.error('⚠️ Failed to create notifications (message still sent):', notificationError);
      }

      return newMessage;
    } catch (error) {
      console.error('❌ Fatal error in sendMessage:', error);
      throw error;
    }
  },

  // Ensure user is in group (add if not already a member)
  async ensureUserInGroup(chatGroupId: string, user: UnifiedUser): Promise<void> {
    console.log('=== ENSURING USER IN GROUP ===');
    console.log('User:', user.username, 'Group:', chatGroupId);
    
    // Check if user is already in group_users
    const { data: existingMembership } = await supabase
      .from('chat_group_users')
      .select('user_id, is_active')
      .eq('chat_group_id', chatGroupId)
      .eq('user_id', user.id)
      .single();

    if (existingMembership?.is_active) {
      console.log('✓ User is already an active member');
      return;
    }

    // Check if user is group creator
    const { data: group } = await supabase
      .from('chat_groups')
      .select('created_by')
      .eq('chat_id', chatGroupId)
      .single();

    if (group?.created_by === user.id) {
      console.log('User is group creator, adding to group_users');
      
      // Add creator to group_users
      const { error } = await supabase
        .from('chat_group_users')
        .upsert({
          chat_group_id: chatGroupId,
          user_id: user.id,
          is_active: true,
          joined_at: new Date().toISOString()
        }, { 
          onConflict: 'chat_group_id,user_id'
        });

      if (error) {
        console.error('Error adding creator to group:', error);
        throw new Error(`Failed to add creator to group: ${error.message}`);
      } else {
        console.log('✓ Creator added to group_users');
      }
    } else {
      // If not creator and not a member, they shouldn't be able to send messages
      throw new Error('User is not a member of this chat group');
    }
  },

  // Validate if user can send messages to group
  async validateUserInGroup(chatGroupId: string, user: UnifiedUser): Promise<boolean> {
    console.log('=== VALIDATING USER IN GROUP ===');
    console.log('User:', user.username, 'Group:', chatGroupId);
    
    try {
      // Check if user is group creator
      const { data: group } = await supabase
        .from('chat_groups')
        .select('created_by')
        .eq('chat_id', chatGroupId)
        .single();

      if (group?.created_by === user.id) {
        console.log('✓ User is group creator');
        return true;
      }

      // Check if user is active member
      const { data: membership } = await supabase
        .from('chat_group_users')
        .select('user_id, is_active')
        .eq('chat_group_id', chatGroupId)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      const isMember = !!membership;
      console.log(isMember ? '✓ User is active member' : '❌ User not in group');
      
      return isMember;
    } catch (error) {
      console.error('❌ Error validating user in group:', error);
      return false;
    }
  },

  // Enhanced message loading with FIXED user profile fetching (ROOT MESSAGES ONLY)
  async getChatMessages(chatGroupId: string, limit?: number): Promise<ChatMessage[]> {
    console.log('=== UNIFIED GET MESSAGES (ROOT ONLY) ===');
    console.log('Group:', chatGroupId, 'Limit:', limit);
    
    let query = supabase
      .from('chat_messages')
      .select('*')
      .eq('chat_group_id', chatGroupId)
      .is('parent_message_id', null)
      .order('created_at', { ascending: true });

    if (limit) {
      query = query.limit(limit);
    }

    console.log('Executing query for chat messages...');
    const { data: messages, error } = await query;
    console.log('Query result:', { messagesCount: messages?.length, error });

    if (error) {
      console.error('❌ Error fetching messages:', error);
      return [];
    }

    if (!messages?.length) {
      console.log('No messages found');
      return [];
    }

    console.log(`Found ${messages.length} messages`);

    // Get user profiles for all message senders - FIXED APPROACH
    const userIds = [...new Set(messages.map(msg => msg.user_id).filter(Boolean))];
    const userProfiles = await this.getUserProfiles(userIds);

    // Enhance messages with user profiles
    const enhancedMessages = messages.map(msg => {
      const profile = userProfiles[msg.user_id];
      console.log(`Message ${msg.message_id}: User ${msg.user_id} -> Profile:`, profile);
      
      return {
        ...msg,
        user_profile: profile || {
          username: `User-${msg.user_id?.slice(0, 8)}`,
          first_name: '',
          last_name: ''
        }
      };
    });

    console.log('✓ Enhanced messages with profiles');
    return enhancedMessages as ChatMessage[];
  },

  // FIXED: Get user profiles from both Supabase and internal users
  async getUserProfiles(userIds: string[]): Promise<Record<string, any>> {
    console.log('=== GETTING USER PROFILES - FIXED ===');
    console.log('User IDs:', userIds);
    
    const profiles: Record<string, any> = {};

    try {
      // Get Supabase profiles - with better field selection
      const { data: supabaseProfiles, error: supabaseError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, role')
        .in('id', userIds);

      if (supabaseError) {
        console.error('Error fetching Supabase profiles:', supabaseError);
      }

      // Get internal user profiles
      const { data: internalUsers, error: internalError } = await supabase
        .from('internal_users')
        .select('id, username, role')
        .in('id', userIds);

      if (internalError) {
        console.error('Error fetching internal users:', internalError);
      }

      // Map Supabase profiles with priority to username
      supabaseProfiles?.forEach(profile => {
        const username = profile.username || profile.first_name || `User-${profile.id.slice(0, 8)}`;
        profiles[profile.id] = {
          username: username,
          first_name: profile.first_name || username,
          last_name: profile.last_name || '',
          role: profile.role || 'player'
        };
        console.log(`Supabase profile mapped: ${profile.id} -> ${username}`);
      });

      // Map internal user profiles
      internalUsers?.forEach(user => {
        const username = user.username || `Internal-${user.id.slice(0, 8)}`;
        profiles[user.id] = {
          username: username,
          first_name: username,
          last_name: '',
          role: user.role || 'player'
        };
        console.log(`Internal user profile mapped: ${user.id} -> ${username}`);
      });

      console.log('✓ Retrieved profiles for', Object.keys(profiles).length, 'users');
      console.log('Final profiles:', profiles);
      
    } catch (error) {
      console.error('❌ Error in getUserProfiles:', error);
    }

    return profiles;
  },

  // Get chat groups for the current user
  async getUserChatGroups(currentUser?: UnifiedUser): Promise<ChatGroup[]> {
    console.log('=== GETTING USER CHAT GROUPS ===');
    
    try {
      // Get the authenticated user - either Supabase or internal
      const { data: { user: supabaseUser } } = await supabase.auth.getUser();
      const activeUser = supabaseUser || currentUser;
      
      if (!activeUser) {
        console.error('❌ No authenticated user found');
        return [];
      }

      console.log(`🔍 Getting groups for user: ${activeUser.id}`);
      console.log(`🔍 User type: ${currentUser ? 'internal' : 'supabase'}`);
      
      // Use the Supabase service which now handles explicit membership filtering  
      const result = await supabaseChatService.getUserChatGroups(activeUser);
      console.log(`📊 Service returned ${result.length} groups:`, result.map(g => g.chat_name));
      return result;
      
    } catch (error) {
      console.error('❌ Complete getUserChatGroups failure:', error);
      return [];
    }
  },

  // CRITICAL FIX: Ensure creator is ALWAYS added to group_users when creating group
  async createChatGroup(chatName: string, currentUser: UnifiedUser, userIds: string[] = []): Promise<ChatGroup | null> {
    console.log('=== CREATING CHAT GROUP - CREATOR MEMBERSHIP FIX ===');
    console.log('Name:', chatName, 'Creator:', currentUser.username, 'Type:', currentUser.userType, 'Users:', userIds);
    
    try {
      // Create the group
      const { data: group, error: groupError } = await supabase
        .from('chat_groups')
        .insert({
          chat_name: chatName,
          created_by: currentUser.id
        })
        .select()
        .single();

      if (groupError) {
        console.error('❌ Error creating chat group:', groupError);
        return null;
      }

      console.log('✓ Chat group created:', group.chat_id);

      // CRITICAL: Add creator to group_users immediately - this was the missing piece
      const { error: creatorError } = await supabase
        .from('chat_group_users')
        .insert({
          chat_group_id: group.chat_id,
          user_id: currentUser.id,
          is_active: true,
          joined_at: new Date().toISOString()
        });

      if (creatorError) {
        console.error('❌ Error adding creator to group_users:', creatorError);
        // Don't fail the whole operation, but log it
      } else {
        console.log('✓ Creator successfully added to group_users');
      }

      // Add other users to group if specified
      if (userIds.length > 0) {
        console.log('Adding additional users to group:', userIds);
        await this.addUsersToGroup(group.chat_id, userIds);
      }

      return group;
    } catch (error) {
      console.error('❌ Fatal error in createChatGroup:', error);
      return null;
    }
  },

  async deleteChatGroup(chatGroupId: string): Promise<void> {
    console.log('=== DELETING CHAT GROUP ===');
    console.log('Group:', chatGroupId);
    
    const { error } = await supabase
      .from('chat_groups')
      .update({ is_active: false })
      .eq('chat_id', chatGroupId);

    if (error) {
      console.error('❌ Error deleting chat group:', error);
      throw error;
    }

    console.log('✓ Chat group deleted');
  },

  // FIXED: Get chat group users with proper profile fetching
  async getChatGroupUsers(chatGroupId: string): Promise<ChatGroupUser[]> {
    console.log('=== GETTING CHAT GROUP USERS - FIXED ===');
    console.log('Group:', chatGroupId);
    
    const { data: groupUsers, error } = await supabase
      .from('chat_group_users')
      .select('*')
      .eq('chat_group_id', chatGroupId)
      .eq('is_active', true);

    if (error) {
      console.error('❌ Error fetching group users:', error);
      return [];
    }

    // Get user details for each group member
    const userIds = groupUsers?.map(gu => gu.user_id) || [];
    const userProfiles = await this.getUserProfiles(userIds);

    const enhancedGroupUsers = (groupUsers || []).map(gu => ({
      ...gu,
      user_details: userProfiles[gu.user_id] || {
        id: gu.user_id,
        username: `User-${gu.user_id.slice(0, 8)}`,
        role: 'player'
      }
    }));

    console.log(`✓ Found ${enhancedGroupUsers.length} group users with profiles`);
    return enhancedGroupUsers;
  },

  async addUsersToGroup(chatGroupId: string, userIds: string[]): Promise<void> {
    console.log('=== ADDING USERS TO GROUP ===');
    console.log('Group:', chatGroupId, 'Users:', userIds);
    
    const groupUsers = userIds.map(userId => ({
      chat_group_id: chatGroupId,
      user_id: userId,
      is_active: true,
      joined_at: new Date().toISOString()
    }));

    const { error } = await supabase
      .from('chat_group_users')
      .insert(groupUsers);

    if (error) {
      console.error('❌ Error adding users to group:', error);
      throw error;
    }

    console.log('✓ Users added to group');
  },

  async leaveGroup(chatGroupId: string, currentUser: UnifiedUser): Promise<void> {
    console.log('=== LEAVING GROUP ===');
    console.log('Group:', chatGroupId, 'User:', currentUser.username);
    
    const { error } = await supabase
      .from('chat_group_users')
      .update({ is_active: false })
      .eq('chat_group_id', chatGroupId)
      .eq('user_id', currentUser.id);

    if (error) {
      console.error('❌ Error leaving group:', error);
      throw error;
    }

    console.log('✓ Left group');
  },

  // FIXED: Get all users from both systems with proper username handling
  async getAllUsers(): Promise<any[]> {
    console.log('=== GETTING ALL USERS - CROSS-SYSTEM COMPATIBILITY ===');
    
    // Get both Supabase and internal users
    const [supabaseResult, internalResult] = await Promise.all([
      supabase.from('profiles').select('id, username, first_name, last_name, role'),
      supabase.from('internal_users').select('id, username, role')
    ]);

    const allUsers: any[] = [];

    // Add Supabase users with proper username handling
    supabaseResult.data?.forEach(user => {
      const username = user.username || user.first_name || `User-${user.id.slice(0, 8)}`;
      allUsers.push({
        id: user.id,
        username: username,
        first_name: user.first_name || username,
        last_name: user.last_name || '',
        role: user.role || 'player',
        user_type: 'supabase'
      });
      console.log(`Added Supabase user: ${username} (${user.id})`);
    });

    // Add internal users
    internalResult.data?.forEach(user => {
      const username = user.username || `Internal-${user.id.slice(0, 8)}`;
      allUsers.push({
        id: user.id,
        username: username,
        first_name: username,
        last_name: '',
        role: user.role || 'player',
        user_type: 'internal'
      });
      console.log(`Added internal user: ${username} (${user.id})`);
    });

    console.log(`✓ Found ${allUsers.length} total users (${supabaseResult.data?.length || 0} Supabase + ${internalResult.data?.length || 0} internal)`);
    
    // Log specific users for debugging
    allUsers.forEach(user => {
      if (user.username.toLowerCase().includes('viana') || user.username.toLowerCase().includes('jbrent')) {
        console.log(`FOUND TARGET USER: ${user.username} (${user.user_type}) - ID: ${user.id}`);
      }
    });
    
    return allUsers;
  },

  async markMessagesAsRead(chatGroupId: string, currentUser?: UnifiedUser): Promise<void> {
    console.log('=== MARKING MESSAGES AS READ ===');
    console.log('Group:', chatGroupId);
    
    let userId = currentUser?.id;
    
    if (!userId) {
      const { data: { user: supabaseUser } } = await supabase.auth.getUser();
      userId = supabaseUser?.id;
    }
    
    if (!userId) {
      console.log('❌ No user found for marking messages as read');
      return;
    }

    console.log('Marking messages as read for user:', userId);

    const { error } = await supabase
      .from('chat_messages')
      .update({ is_read: true })
      .eq('chat_group_id', chatGroupId)
      .neq('user_id', userId);

    if (error) {
      console.error('❌ Error marking messages as read:', error);
    } else {
      console.log('✓ Messages marked as read successfully');
    }
  },

  // FIXED: Create notifications for all group members with proper mention detection
  async createNotificationsForMessage(message: ChatMessage, chatGroupId: string, sender: UnifiedUser): Promise<void> {
    console.log('=== CREATING NOTIFICATIONS - MEMBERSHIP FILTERED ===');
    console.log('Sender:', sender.username, 'Type:', sender.userType);
    console.log('Message:', message.message);
    
    try {
      // Get all group members except sender - this ensures only actual members get notifications
      const { data: groupMembers } = await supabase
        .from('chat_group_users')
        .select('user_id')
        .eq('chat_group_id', chatGroupId)
        .eq('is_active', true)
        .neq('user_id', sender.id);

      console.log('Found', groupMembers?.length || 0, 'active group members (excluding sender)');

      if (!groupMembers?.length) {
        console.log('No other members to notify');
        return;
      }

      // Get group name
      const { data: group } = await supabase
        .from('chat_groups')
        .select('chat_name')
        .eq('chat_id', chatGroupId)
        .single();

      const groupName = group?.chat_name || 'Unknown Group';

      // Get member profiles for user type information (cross-system)
      const memberIds = groupMembers.map(m => m.user_id);
      const memberProfiles = await this.getUserProfiles(memberIds);

      // Use triggerMentionNotifications for consistent mention handling
      const { triggerMentionNotifications } = await import('../../utils/notificationTriggers');

      // Prepare group members with proper user type information
      const allGroupMembers = groupMembers.map(member => {
        const memberProfile = memberProfiles[member.user_id];
        return {
          user_id: member.user_id,
          userType: (memberProfile?.userType || 'internal') as 'supabase' | 'internal'
        };
      });

      console.log(`💬 Processing mentions for message with ${allGroupMembers.length} group members`);
      
      // Use the centralized mention notification system
      await triggerMentionNotifications({
        message: message.message,
        from_user_id: sender.id,
        from_username: sender.username,
        chat_group_id: chatGroupId,
        message_id: message.message_id,
        chat_group_name: groupName,
        allGroupMembers
      });

      console.log('✓ Cross-system notifications created for all members');
    } catch (error) {
      console.error('❌ Error creating notifications:', error);
    }
  },

  // Helper method to determine user type
  async getUserType(userId: string): Promise<'supabase' | 'internal'> {
    // Check if user exists in internal_users table
    const { data: internalUser } = await supabase
      .from('internal_users')
      .select('id')
      .eq('id', userId)
      .eq('is_active', true)
      .single();
    
    if (internalUser) {
      return 'internal';
    }
    
    // Check if user exists in profiles table (supabase users)
    const { data: supabaseUser } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId)
      .single();
    
    return supabaseUser ? 'supabase' : 'internal'; // Default to internal if not found
  },

  // Increment reply count for a parent message
  async incrementReplyCount(messageId: string): Promise<void> {
    console.log('=== INCREMENT REPLY COUNT ===');
    console.log('Message ID:', messageId);
    
    try {
      const { error } = await supabase.rpc('increment_reply_count', { 
        p_message_id: messageId 
      });
      
      if (error) {
        console.error('❌ Error incrementing reply count:', error);
      } else {
        console.log('✓ Reply count incremented');
      }
    } catch (error) {
      console.error('❌ Fatal error incrementing reply count:', error);
    }
  },

  // Get thread replies for a parent message
  async getThreadReplies(parentMessageId: string): Promise<ChatMessage[]> {
    console.log('=== GET THREAD REPLIES ===');
    console.log('Parent message ID:', parentMessageId);
    
    try {
      const { data: replies, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('parent_message_id', parentMessageId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('❌ Error fetching thread replies:', error);
        return [];
      }

      if (!replies?.length) {
        console.log('No replies found');
        return [];
      }

      console.log(`Found ${replies.length} replies`);

      // Get user profiles for reply senders
      const userIds = [...new Set(replies.map(msg => msg.user_id).filter(Boolean))];
      const userProfiles = await this.getUserProfiles(userIds);

      // Enhance replies with user profiles
      const enhancedReplies = replies.map(msg => ({
        ...msg,
        user_profile: userProfiles[msg.user_id] || {
          username: `User-${msg.user_id?.slice(0, 8)}`,
          first_name: '',
          last_name: ''
        }
      }));

      console.log('✓ Enhanced replies with profiles');
      return enhancedReplies as ChatMessage[];
    } catch (error) {
      console.error('❌ Error in getThreadReplies:', error);
      return [];
    }
  }

};
